import React, { useState, useEffect, useRef } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Legend, ComposedChart
} from 'recharts';
import { AURAVEX_LOGO } from './logo';
import { INR, PLANS, Badge, Dot, KPI, Panel, Prog, Pill, BtnStyles, Btn, Inp, Sel, Textarea, Modal, Toast, DataTable, PageHeader, AlertBox, Grid, Divider, CameraLiveModal, co2Data, perfWeeks, radarData} from './utils';

// ============================================================
// LIVE API INTEGRATION - AURAVEX Backend
// ============================================================
const API_NODE   = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL : '';
const API_PYTHON = (typeof process !== 'undefined' && process.env?.REACT_APP_PYTHON_URL)
  ? process.env.REACT_APP_PYTHON_URL : 'http://localhost:8000';
const WS_URL_BASE = (typeof process !== 'undefined' && process.env?.REACT_APP_WS_URL)
  ? process.env.REACT_APP_WS_URL : 'ws://localhost:5000/ws';

const apiFetch = async (path, opts = {}, base = API_NODE) => {
  try {
    const token = localStorage.getItem('auravex_token');
    const res = await fetch(base + path, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...opts.headers,
      },
      ...opts,
    });
    if (res.status === 401) {
      localStorage.removeItem('auravex_token');
      localStorage.removeItem('auravex_user');
      window.dispatchEvent(new CustomEvent('auravex:401'));
      return null;
    }
    if (!res.ok) {
      let errMsg = `HTTP ${res.status}`;
      try { const j = await res.json(); errMsg = j.error || j.message || errMsg; } catch {}
      throw new Error(errMsg);
    }
    return await res.json();
  } catch (e) {
    console.warn(`[API] ${opts.method||'GET'} ${path}:`, e.message);
    throw e;  // re-throw so callers can handle it
  }
};
const pyFetch   = (p, o={}) => apiFetch(p, o, API_PYTHON);
const apiPost   = (p, d) => apiFetch(p, { method: 'POST',   body: JSON.stringify(d) });
const apiPut    = (p, d) => apiFetch(p, { method: 'PUT',    body: JSON.stringify(d) });
const apiPatch  = (p, d) => apiFetch(p, { method: 'PATCH',  body: JSON.stringify(d) });
const apiDelete = (p)    => apiFetch(p, { method: 'DELETE' });

// Map DB snake_case -> frontend camelCase
const mapTenant = r => r ? ({
  ...r,
  plan:    r.plan_id   ?? r.plan   ?? 'starter',
  maxCam:  r.max_cameras ?? r.max_cam ?? r.maxCam ?? 0,
  maxU:    r.max_users   ?? r.max_u   ?? r.maxU   ?? 0,
}) : null;
const mapCamera = r => r ? ({
  ...r,
  siteId:  r.site_id    ?? r.siteId  ?? '',
  res:     r.resolution ?? r.res     ?? '1080p',
  models:  Array.isArray(r.models) ? r.models : (typeof r.models==='string' ? JSON.parse(r.models||'[]') : []),
}) : null;
const mapEvent  = r => r ? ({
  ...r,
  caseId:  r.case_id   ?? r.caseId  ?? '',
  tId:     r.tenant_id ?? r.t_id    ?? r.tId    ?? '',
  siteId:  r.site_id   ?? r.siteId  ?? '',
  camId:   r.cam_id    ?? r.camId   ?? '',
  sev:     r.severity  ?? r.sev     ?? 'Medium',
  evidence: Array.isArray(r.evidence) ? r.evidence : [],
}) : null;
const mapUser   = r => r ? ({
  id:        r.id,
  name:      r.name,
  email:     r.email,
  role:      r.role,
  tenantId:  r.tenant_id   ?? r.tenantId ?? '',
  status:    r.status      ?? 'Active',
  mfa:       r.mfa_enabled ?? r.mfa      ?? false,
  last:      r.last_login  ?? r.last_seen ?? r.last ?? '',
  mobiles:   Array.isArray(r.mobiles)  ? r.mobiles  : (typeof r.mobiles ==='string' ? JSON.parse(r.mobiles  ||'[]') : []),
  emails:    Array.isArray(r.emails)   ? r.emails   : (typeof r.emails  ==='string' ? JSON.parse(r.emails   ||'[]') : [r.email||'']),
  notifyVia: (typeof r.notify_via==='object' && r.notify_via!==null) ? r.notify_via : (typeof r.notify_via==='string' ? JSON.parse(r.notify_via||'{}') : r.notifyVia || {email:true}),
}) : null;
const mapJetson = r => r ? ({ ...r, siteId: r.site_id??r.siteId, tId: r.tenant_id??r.t_id??r.tId }) : null;
const mapViol   = r => r ? ({ ...r, camId: r.cam_id??r.camId }) : null;
/* -- API payload converters (Frontend → Backend field names) -- */
const tenantToAPI  = t => ({ name:t.name, sector:t.sector, plan_id:t.plan||t.plan_id||'starter', contact:t.contact||'', billing:t.billing||'monthly', renewal:t.renewal||null, status:t.status, onboarded:t.onboarded||false });
const cameraToAPI  = c => ({ site_id:c.siteId||c.site_id, name:c.name, brand:c.brand||'Hikvision', type:c.type||'Fixed', ip:c.ip||'', resolution:c.res||c.resolution||'1080p', fps:c.fps||25, models:c.models||[], retention:c.retention||'30d', zone:c.zone||'', status:c.status, health:c.health });
const userToAPI    = u => ({ name:u.name, email:u.email||(u.emails&&u.emails[0])||'', role:u.role, tenant_id:u.tenantId||u.tenant_id||null, mobiles:u.mobiles||[], emails_list:u.emails||[], notify_via:u.notifyVia||u.notify_via||{email:true}, mfa_enabled:u.mfa||false, status:u.status });
const ruleToAPI    = r => ({ name:r.name, trigger:r.trigger||'', condition:r.condition||'', action:r.action||'', tenant_id:r.tId||r.tenant_id||'All', status:r.status||'Active' });

/* -- SECTOR AVATARS ------------------------------------------- */
const AVATAR_GOV  = "data:image/jpeg;base64,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";   // Male  - Government
const AVATAR_PVT  = "data:image/jpeg;base64,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"; // Female - Private / Industrial
const sectorAvatar = (sector) =>
  (sector==="Government"||sector==="Municipal"||sector==="Defence"||sector==="Police")
    ? AVATAR_GOV : AVATAR_PVT;

/* -- DESIGN TOKENS -- CLEAN WHITE LIGHT THEME ------------------- */
const T = {
  /* -- Surface hierarchy -- */
  bg:"#F0F2F7",     /* Page background -- very light grey-blue */
  deep:"#FFFFFF",   /* Sidebar background */
  panel:"#FFFFFF",  /* Card panels */
  card:"#FFFFFF",   /* Inline cards */
  border:"#E2E8F3", /* Subtle borders */
  hi:"#EEF2FB",     /* Hover highlight */
  glow:"#DDE5F6",   /* Active glow */
  faint:"#F7F9FD",  /* Input backgrounds */
  /* Gold primary -- AURAVEX brand */
  accent:"#C48A0E", accentD:"#9A6B00", accentLo:"#C48A0E18",
  /* Electric blue */
  blue:"#1A5FB4",   blueLo:"#1A5FB414",
  /* Green */
  green:"#1E8A55",  greenLo:"#1E8A5514",
  /* Standard semantic */
  red:"#C0392B",    redLo:"#C0392B12",
  purple:"#6527BE", purpleLo:"#6527BE12",
  teal:"#0E7490",   tealLo:"#0E749012",
  orange:"#C05800", orangeLo:"#C0580012",
  pink:"#A21060",   pinkLo:"#A2106012",
  /* Text -- dark on white */
  text:"#1A2235",   muted:"#7A90B0",
  /* Silver */
  silver:"#4A607A",
  font:"'DM Sans',system-ui,sans-serif",
  mono:"'IBM Plex Mono','Fira Mono',monospace",
};

export const PageUsers = ({users,setUsers,tenants,toast,sendNotification,logAudit}) => {
  const emptyForm = {
    name:"", role:"Viewer", tenantId:"", mfaMethod:"TOTP",
    emails:[""],          // array of email addresses (first is primary)
    mobiles:[""],         // array of mobile numbers
    notifyVia:{email:true, sms:false, whatsapp:false},
  };
  const [showAdd,  setShowAdd]  = useState(false);
  const [editU,    setEditU]    = useState(null);
  const [delU,     setDelU]     = useState(null);
  const [sendNotifModal, setSendNotifModal] = useState(null); // user to send custom notif to
  const [customMsg, setCustomMsg] = useState({subject:"",body:"",channels:{email:true,sms:false,whatsapp:false}});
  const [form,     setForm]     = useState(emptyForm);
  const ff = k => v => setForm(p=>({...p,[k]:v}));
  const ef = k => v => setEditU(p=>({...p,[k]:v}));

  /* -- Dynamic email/mobile list helpers ----------------- */
  const addEmail   = (which)  => { const upd = p=>({...p, emails:[...p.emails,""]}); which==="form"?setForm(upd):setEditU(upd); };
  const rmEmail    = (which,i)=> { const upd = p=>({...p, emails:p.emails.filter((_,j)=>j!==i)}); which==="form"?setForm(upd):setEditU(upd); };
  const setEmail   = (which,i,v)=>{ const upd = p=>({...p, emails:p.emails.map((e,j)=>j===i?v:e)}); which==="form"?setForm(upd):setEditU(upd); };
  const addMobile  = (which)  => { const upd = p=>({...p, mobiles:[...p.mobiles,""]}); which==="form"?setForm(upd):setEditU(upd); };
  const rmMobile   = (which,i)=> { const upd = p=>({...p, mobiles:p.mobiles.filter((_,j)=>j!==i)}); which==="form"?setForm(upd):setEditU(upd); };
  const setMobile  = (which,i,v)=>{ const upd = p=>({...p, mobiles:p.mobiles.map((m,j)=>j===i?v:m)}); which==="form"?setForm(upd):setEditU(upd); };
  const setNotify  = (which,ch,v)=>{ const upd = p=>({...p, notifyVia:{...p.notifyVia,[ch]:v}}); which==="form"?setForm(upd):setEditU(upd); };

  /* -- CRUD ---------------------------------------------- */
  const [otpSent, setOtpSent]   = useState(false);
  const [otpVal,  setOtpVal]    = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [devOtpUser, setDevOtpUser] = useState("");

  const sendUserOtp = async () => {
    const primaryMobile = form.mobiles.filter(Boolean)?.[0]||"";
    if(!primaryMobile){ toast("Mobile number required to send OTP","warn"); return; }
    setOtpLoading(true);
    try {
      const r = await apiPost('/api/otp/add-user-otp', { mobile: primaryMobile, name: form.name });
      if(r?.success) {
        setOtpSent(true);
        if(r.dev_otp) setDevOtpUser(r.dev_otp);if(r.fallback_otp) setDevOtpUser(r.fallback_otp);
        toast("OTP sent to "+primaryMobile);
      } else { toast(r?.error||"Failed to send OTP","warn"); }
    } catch(e) { toast("OTP send failed: "+e.message,"warn"); }
    setOtpLoading(false);
  };

  const add = () => {
    if(!form.name.trim()){ toast("Name is required","warn"); return; }
    const primaryEmail  = form.emails.filter(Boolean)?.[0]||"";
    const primaryMobile = form.mobiles.filter(Boolean)?.[0]||"";
    const chs = Object.entries(form.notifyVia).filter(([,v])=>v).map(([k])=>k);
    const newU = {
      id:`U${String(Date.now()).slice(-4)}`,
      name:form.name.trim(), email:primaryEmail,
      emails:form.emails.filter(e=>e.trim()), mobiles:form.mobiles.filter(m=>m.trim()),
      notifyVia:form.notifyVia,
      role:form.role, tenantId:form.tenantId, status:"Active", mfa:false, last:"Just now"
    };
    setUsers(p=>[...p,newU]);
    apiPost('/api/users', userToAPI(newU)).then(saved=>{
      if(saved?.id) setUsers(p=>p.map(u=>u.id===newU.id?{...newU,...mapUser(saved)}:u));
    }).catch(e=>toast("Save failed: "+e.message,"warn"));
    // Send welcome notification if we have contact info
    if(primaryEmail || primaryMobile){
      sendNotification&&sendNotification(
        primaryEmail||primaryMobile,
        "Welcome to AURAVEX GLOBAL AI",
        `Dear ${form.name},\n\nYour AURAVEX GLOBAL AI account has been created.\nRole: ${form.role}\nLogin: https://auravex.ai/login\n\nPlease set up your password and MFA on first login.\n\nTeam AURAVEX GLOBAL AI`,
        chs.length?chs:["email"], newU
      );
    }
    logAudit&&logAudit("USER_CREATED",form.name,"Info");
    setShowAdd(false); setForm(emptyForm); setOtpSent(false); setOtpVal(""); setDevOtpUser("");
    toast(`✅ ${form.name} created${(primaryEmail||primaryMobile)?" -- welcome notification sent":""}`);
  };

  const saveEdit = () => {
    if(!editU.name.trim()){ toast("Name is required","warn"); return; }
    const cleanEmails  = (editU.emails||[]).filter(e=>e.trim());
    const cleanMobiles = (editU.mobiles||[]).filter(m=>m.trim());
    const primaryEmail  = cleanEmails[0]  || editU.email  || "";
    const primaryMobile = cleanMobiles[0] || editU.mobile || "";
    const chs = Object.entries(editU.notifyVia||{}).filter(([,v])=>v).map(([k])=>k);
    const updated = {...editU, email:primaryEmail, emails:cleanEmails, mobiles:cleanMobiles};
    setUsers(p=>p.map(u=>u.id===editU.id?updated:u));
    apiPut(`/api/users/${editU.id}`, userToAPI(updated)).catch(e=>toast("Save failed: "+e.message,"warn"));
    if(primaryEmail || primaryMobile){
      sendNotification&&sendNotification(
        primaryEmail||primaryMobile,
        "AURAVEX GLOBAL AI: Account Updated",
        `Dear ${editU.name},\n\nYour AURAVEX GLOBAL AI account details have been updated.\nRole: ${editU.role}\n\nIf you did not request this, contact admin@auravex.ai.\n\nTeam AURAVEX GLOBAL AI`,
        chs.length?chs:["email"], updated
      );
    }
    logAudit&&logAudit("USER_EDITED",editU.name,"Info");
    setEditU(null); toast(`✅ ${editU.name} updated${(primaryEmail||primaryMobile)?" -- notification sent":""}`);
  };

  const delUser = () => {
    const chs = Object.entries(delU.notifyVia||{}).filter(([,v])=>v).map(([k])=>k);
    sendNotification(delU.email,"AURAVEX GLOBAL AI: Account Removed",
      `Dear ${delU.name},\n\nYour AURAVEX GLOBAL AI account has been removed.\n\nContact admin@auravex.ai if this was a mistake.\n\nTeam AURAVEX GLOBAL AI`,
      chs.length?chs:["email"], delU
    );
    setUsers(p=>p.filter(u=>u.id!==delU.id));
    apiDelete(`/api/users/${delU.id}`).catch(e=>toast("Delete failed: "+e.message,"warn"));
    logAudit&&logAudit("USER_DELETED",delU.name,"High");
    setDelU(null); toast(`${delU.name} deleted -- notification sent`,"warn");
  };

  const toggleLock = id => {
    const u = users.find(x=>x.id===id);
    const newS = u.status==="Active"?"Locked":"Active";
    setUsers(p=>p.map(x=>x.id===id?{...x,status:newS}:x));
    apiPatch(`/api/users/${id}/status`,{status:newS}).catch(()=>{});
    const chs = Object.entries(u.notifyVia||{email:true}).filter(([,v])=>v).map(([k])=>k);
    sendNotification(u.email,`AURAVEX GLOBAL AI: Account ${newS}`,
      `Dear ${u.name},\n\nYour account has been ${newS.toLowerCase()} by the administrator.\n\nTeam AURAVEX GLOBAL AI`,
      chs.length?chs:["email"], u
    );
    logAudit&&logAudit(`USER_${newS.toUpperCase()}`,u.name,"High");
    toast(`${u.name} ${newS} -- notification sent`);
  };
  const toggleMfa = id => {
    const u = users.find(x=>x.id===id);
    const newMfa = !u?.mfa;
    setUsers(p=>p.map(x=>x.id===id?{...x,mfa:newMfa}:x));
    apiPatch(`/api/users/${id}/mfa`,{mfa_enabled:newMfa}).catch(()=>{});
    toast("MFA updated");
  };

  /* -- Send Custom Notification --------------------------- */
  const doSendCustom = () => {
    const chs = Object.entries(customMsg.channels).filter(([,v])=>v).map(([k])=>k);
    if(!chs.length){ toast("Please select at least one channel","warn"); return; }
    if(!customMsg.subject){ toast("Please enter a subject","warn"); return; }
    if(!customMsg.body){ toast("Please enter a message body","warn"); return; }
    // Use primary email/mobile as the "to" address
    const primaryEmail  = (sendNotifModal.emails?.filter(Boolean)?.[0] || sendNotifModal.email || "");
    const primaryMobile = (sendNotifModal.mobiles?.filter(Boolean)?.[0] || "");
    // Build a user-like object for sendNotification
    const recipientUser = {
      ...sendNotifModal,
      email: primaryEmail,
      emails: sendNotifModal.emails?.filter(Boolean) || (primaryEmail?[primaryEmail]:[]),
      mobiles: sendNotifModal.mobiles?.filter(Boolean) || (primaryMobile?[primaryMobile]:[]),
      notifyVia: {
        email:     chs.includes("email"),
        sms:       chs.includes("sms"),
        whatsapp:  chs.includes("whatsapp"),
      }
    };
    sendNotification(primaryEmail || primaryMobile || sendNotifModal.name,
      customMsg.subject, customMsg.body, chs, recipientUser);
    setSendNotifModal(null);
    setCustomMsg({subject:"",body:"",channels:{email:true,sms:false,whatsapp:false}});
    toast("✅ Notification sent to "+sendNotifModal.name);
  };

  const ROLES=[
    {r:"Super Admin", c:T.red,    perms:["Full System","All Tenants","Billing","Users","Deploy"]},
    {r:"Tenant Admin",c:T.purple, perms:["Own Tenant","All Cameras","Rules","Reports","Users"]},
    {r:"Operator",    c:T.blue,   perms:["Own Tenant","Cameras","Alerts","Rules View","Reports"]},
    {r:"Analyst",     c:T.teal,   perms:["Own Tenant","Dashboard","Reports","No Config"]},
    {r:"Viewer",      c:T.green,  perms:["Own Tenant","Dashboard","Read Only","No Export"]}
  ];

  /* -- Multi-field helper component ---------------------- */
  const MultiField = ({label,values,placeholder,onAdd,onRemove,onChange,type="text"}) => (
    <div style={{display:"flex",flexDirection:"column",gap:5}}>
      <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:.8,textTransform:"uppercase",marginBottom:2}}>{label}</div>
      {values.map((v,i)=>(
        <div key={i} style={{display:"flex",gap:5,alignItems:"center"}}>
          <input value={v} type={type} onChange={e=>onChange(i,e.target.value)} placeholder={`${placeholder} ${i+1}`}
            style={{flex:1,background:T.faint,border:`1px solid ${T.border}`,borderRadius:6,padding:"7px 10px",fontSize:11,color:T.text,fontFamily:T.font,outline:"none"}}/>
          {values.length>1&&<button onClick={()=>onRemove(i)} style={{background:T.redLo,border:`1px solid ${T.red}44`,borderRadius:5,padding:"4px 8px",cursor:"pointer",fontSize:10,color:T.red,fontFamily:T.font}}>✕</button>}
        </div>
      ))}
      <button onClick={onAdd} style={{background:"transparent",border:`1px dashed ${T.border}`,borderRadius:6,padding:"5px 10px",cursor:"pointer",fontSize:10,color:T.blue,fontFamily:T.font,textAlign:"left"}}>
        + Add another {label.toLowerCase()}
      </button>
    </div>
  );

  /* -- Notify Channel Toggle ------------------------------ */
  const NotifyChannels = ({which,notifyVia}) => (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:.8,textTransform:"uppercase"}}>Notification Channels <span style={{color:T.muted,fontWeight:400}}>(optional)</span></div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {[{k:"email",icon:"📧",label:"Email"},{k:"sms",icon:"💬",label:"SMS"},{k:"whatsapp",icon:"📱",label:"WhatsApp"}].map(({k,icon,label})=>(
          <button key={k} onClick={()=>setNotify(which,k,!notifyVia[k])}
            style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:7,border:`1px solid ${notifyVia[k]?T.green+"55":T.border}`,background:notifyVia[k]?T.greenLo:"transparent",cursor:"pointer",fontFamily:T.font,fontSize:10,color:notifyVia[k]?T.green:T.muted,transition:"all .12s"}}>
            <span>{icon}</span><span>{label}</span>
            <span style={{fontWeight:800,color:notifyVia[k]?T.green:T.muted}}>{notifyVia[k]?"✓":"○"}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <PageHeader title="Users & Access Control (RBAC)" sub="Role-based access - Multi-contact - MFA enforcement - Per-channel notification preferences - SSO-ready">
        <Btn onClick={()=>setShowAdd(true)}>+ Add User</Btn>
      </PageHeader>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        <KPI label="Total Users"  value={users.length}                                       icon="👥" color={T.blue}/>
        <KPI label="Active"       value={users.filter(u=>u.status==="Active").length}         icon="✅" color={T.green}/>
        <KPI label="MFA Enabled"  value={`${users.filter(u=>u.mfa).length}/${users.length}`} icon="🔐" color={T.teal}/>
        <KPI label="Locked"       value={users.filter(u=>u.status==="Locked").length}         icon="🔒" color={T.red}/>
      </div>

      <Panel title="ROLE HIERARCHY">
        <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
          {ROLES.map(({r,c,perms})=>(
            <div key={r} style={{background:T.deep,borderRadius:8,padding:10,border:`1px solid ${c}28`}}>
              <div style={{fontSize:10,fontWeight:800,color:c,marginBottom:8}}>{r}</div>
              {perms.map(p=><div key={p} style={{fontSize:9,color:T.muted,marginBottom:3,paddingLeft:7,borderLeft:`2px solid ${c}55`}}>* {p}</div>)}
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="ALL USERS">
        <DataTable cols={[
          {k:"name",l:"Name",r:(v,row)=>{
            // Determine avatar: Government tenants have IDs starting with T001/T002 (govt sectors)
            const isGov = row.tenantId==="T001"||row.tenantId==="T005"||row.tenantId==="T007";
            return(
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <img src={isGov?AVATAR_GOV:AVATAR_PVT} alt={v}
                  style={{width:28,height:28,borderRadius:"50%",objectFit:"cover",objectPosition:"top",border:`1.5px solid ${isGov?T.blue+"55":T.teal+"55"}`,flexShrink:0}}/>
                <span style={{fontWeight:700,fontSize:11}}>{v}</span>
              </div>
            );
          }},
          {k:"emails",   l:"Emails",       r:v=><div>{(v&&v.length?v:["-"]).map((e,i)=><div key={i} style={{fontFamily:T.mono,fontSize:9,color:T.muted}}>{e||"-"}</div>)}</div>},
          {k:"mobiles",  l:"Mobiles",      r:v=><div>{(v&&v.length?v:["-"]).map((m,i)=><div key={i} style={{fontFamily:T.mono,fontSize:9,color:T.muted}}>{m||"-"}</div>)}</div>},
          {k:"notifyVia",l:"Notify Via",   r:(v,row)=><div style={{display:"flex",gap:3,alignItems:"center"}}>{(v?.email||row.email)&&<Badge text="Email" xs color={T.blue}/>}{v?.sms&&<Badge text="SMS" xs color={T.orange}/>}{v?.whatsapp&&<Badge text="WA" xs color={T.teal}/>}{!(v?.email)&&!row.email&&<span style={{fontSize:9,color:T.muted}}>--</span>}</div>},
          {k:"role",     l:"Role",         r:v=><Badge text={v} sm color={{"Tenant Admin":T.purple,Operator:T.blue,Analyst:T.teal,Viewer:T.green,"Super Admin":T.red}[v]||T.blue}/>},
          {k:"mfa",      l:"MFA",          r:v=><Badge text={v?"ON":"OFF"} color={v?T.green:T.red} sm/>},
          {k:"status",   l:"Status",       r:v=><Dot s={v}/>},
          {k:"id",       l:"Actions",      r:(v,row)=><div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
            <Btn sm v="blue"    onClick={()=>{setSendNotifModal(row);setCustomMsg({subject:"",body:"",channels:{email:!!(row.notifyVia?.email||row.email),sms:!!row.notifyVia?.sms,whatsapp:!!row.notifyVia?.whatsapp}});}}>📤 Notify</Btn>
            <Btn sm v="ghost"   onClick={()=>toggleMfa(v)}>{row.mfa?"Disable":"Enable"} MFA</Btn>
            <Btn sm v="ghost"   onClick={()=>setEditU({...row,emails:row.emails||[row.email||""],mobiles:row.mobiles||[row.mobile||""],notifyVia:row.notifyVia||{email:true,sms:false,whatsapp:false}})}>✏ Edit</Btn>
            <Btn sm v={row.status==="Active"?"danger":"success"} onClick={()=>toggleLock(v)}>{row.status==="Active"?"Lock":"Unlock"}</Btn>
            <Btn sm v="danger"  onClick={()=>setDelU(row)}>🗑</Btn>
          </div>},
        ]} rows={users}/>
      </Panel>

      {/* -- ADD USER MODAL --------------------------------------- */}
      <Modal open={showAdd} title="+ Add New User" onClose={()=>{setShowAdd(false);setForm(emptyForm);}}>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Grid cols={2} gap={12}>
            <Inp label="Full Name"  value={form.name}     onChange={ff("name")}     placeholder="Rajesh Sharma"/>
            <Sel label="Role"       value={form.role}     onChange={ff("role")}     options={["Super Admin","Tenant Admin","Operator","Analyst","Viewer"]}/>
            <Sel label="Tenant"     value={form.tenantId} onChange={ff("tenantId")} options={(tenants||[]).map(t=>({v:t.id,l:t.name}))}/>
            <Sel label="MFA Method" value={form.mfaMethod||"TOTP"} onChange={ff("mfaMethod")} options={["TOTP App","SMS OTP","Email OTP","Hardware Key"]}/>
          </Grid>
          <MultiField label="Email Addresses" values={form.emails} placeholder="Email" type="email"
            onAdd={()=>addEmail("form")} onRemove={i=>rmEmail("form",i)} onChange={(i,v)=>setEmail("form",i,v)}/>
          <MultiField label="Mobile Numbers (OTP Activation)" values={form.mobiles} placeholder="+91 9XXXXXXXXX" type="tel"
            onAdd={()=>addMobile("form")} onRemove={i=>rmMobile("form",i)} onChange={(i,v)=>{setMobile("form",i,v); setOtpSent(false); setOtpVal(""); setDevOtpUser("");}}/>
          
          {/* OTP Section */}
          {form.mobiles.filter(m=>m.trim()).length>0 && (
            <div style={{padding:"12px 14px",background:"rgba(196,138,14,0.06)",border:"1px solid rgba(196,138,14,0.2)",borderRadius:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:otpSent?10:0}}>
                <div style={{fontSize:11,fontWeight:700,color:T.muted,letterSpacing:.8}}>MOBILE VERIFICATION (OTP)</div>
                {!otpSent
                  ? <Btn v="primary" sm onClick={sendUserOtp} disabled={otpLoading}>{otpLoading?"Sending...":"📱 Send OTP"}</Btn>
                  : <span style={{fontSize:10,color:T.green,fontWeight:700}}>✅ OTP Sent</span>
                }
              </div>
              {otpSent && (
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {devOtpUser && <div style={{padding:"6px 10px",background:"rgba(34,197,94,0.1)",border:"1px solid rgba(34,197,94,0.3)",borderRadius:6,fontSize:11,color:"#22C55E"}}>
                    🧪 DEV OTP: <span style={{fontFamily:"monospace",fontWeight:800,cursor:"pointer"}} onClick={()=>setOtpVal(devOtpUser)}>{devOtpUser}</span> (click to fill)
                  </div>}
                  <input value={otpVal} onChange={e=>setOtpVal(e.target.value.replace(/[^0-9]/g,"").slice(0,6))}
                    placeholder="Enter 6-digit OTP to verify" maxLength={6}
                    style={{padding:"10px 14px",borderRadius:8,background:"rgba(255,255,255,0.07)",border:`1px solid ${otpVal.length===6?T.green:T.border}`,color:T.text,fontSize:16,fontFamily:"monospace",letterSpacing:6,textAlign:"center",width:"100%",outline:"none"}}/>
                  <div style={{fontSize:10,color:T.muted}}>After creating the user, they will receive an SMS with a link to set their password using this OTP.</div>
                </div>
              )}
              {!otpSent && <div style={{fontSize:10,color:T.muted,marginTop:4}}>Send OTP to verify the user&#39;s mobile number before creating their account.</div>}
            </div>
          )}

          <NotifyChannels which="form" notifyVia={form.notifyVia}/>
          <AlertBox type="info">i️ Email &amp; mobile are optional. Send OTP to verify mobile. User will set their own password on first login.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>{setShowAdd(false);setForm(emptyForm); setOtpSent(false); setOtpVal(""); setDevOtpUser("");}}>Cancel</Btn>
            <Btn onClick={add} disabled={!form.name}>+ Create User</Btn>
          </div>
        </div>
      </Modal>

      {/* -- EDIT USER MODAL -------------------------------------- */}
      <Modal open={!!editU} title={`✏ Edit User -- ${editU?.name}`} onClose={()=>setEditU(null)}>
        {editU&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Grid cols={2} gap={12}>
            <Inp label="Full Name" value={editU.name}     onChange={ef("name")}     placeholder="Full name"/>
            <Sel label="Role"      value={editU.role}     onChange={ef("role")}     options={["Super Admin","Tenant Admin","Operator","Analyst","Viewer"]}/>
            <Sel label="Tenant"    value={editU.tenantId} onChange={ef("tenantId")} options={(tenants||[]).map(t=>({v:t.id,l:t.name}))}/>
            <Sel label="Status"    value={editU.status}   onChange={ef("status")}   options={["Active","Locked"]}/>
          </Grid>
          <MultiField label="Email Addresses" values={editU.emails||[editU.email||""]} placeholder="Email" type="email"
            onAdd={()=>addEmail("edit")} onRemove={i=>rmEmail("edit",i)} onChange={(i,v)=>setEmail("edit",i,v)}/>
          <MultiField label="Mobile Numbers" values={editU.mobiles||[""]} placeholder="+91 9XXXXXXXXX" type="tel"
            onAdd={()=>addMobile("edit")} onRemove={i=>rmMobile("edit",i)} onChange={(i,v)=>setMobile("edit",i,v)}/>
          <NotifyChannels which="edit" notifyVia={editU.notifyVia||{email:true,sms:false,whatsapp:false}}/>
          <AlertBox type="info">i️ A notification will be sent via preferred channels on save.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setEditU(null)}>Cancel</Btn>
            <Btn onClick={saveEdit}>💾 Save &amp; Notify</Btn>
          </div>
        </div>}
      </Modal>

      {/* -- DELETE USER MODAL ------------------------------------ */}
      <Modal open={!!delU} title="⚠️ Delete User" onClose={()=>setDelU(null)}>
        {delU&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
          <AlertBox type="error">🗑 Delete <strong>{delU.name}</strong>? They will lose all access immediately.</AlertBox>
          <AlertBox type="info">Notification will be sent via their preferred channels.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setDelU(null)}>Cancel</Btn>
            <Btn v="danger" onClick={delUser}>🗑 Delete &amp; Notify</Btn>
          </div>
        </div>}
      </Modal>

      {/* -- SEND CUSTOM NOTIFICATION MODAL ----------------------- */}
      <Modal open={!!sendNotifModal} title={`📤 Send Notification -- ${sendNotifModal?.name}`} onClose={()=>setSendNotifModal(null)}>
        {sendNotifModal&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{background:T.faint,borderRadius:8,padding:10,border:`1px solid ${T.border}`}}>
            <div style={{fontSize:10,fontWeight:700,color:T.text,marginBottom:6}}>Delivery Channels</div>
            <div style={{display:"flex",gap:8}}>
              {[{k:"email",icon:"📧",label:"Email",avail:!!(sendNotifModal.emails?.length||sendNotifModal.email)},
                {k:"sms",  icon:"💬",label:"SMS",  avail:!!(sendNotifModal.mobiles?.length)},
                {k:"whatsapp",icon:"📱",label:"WhatsApp",avail:!!(sendNotifModal.mobiles?.length)}
              ].map(({k,icon,label,avail})=>(
                <button key={k} onClick={()=>avail&&setCustomMsg(p=>({...p,channels:{...p.channels,[k]:!p.channels[k]}}))}
                  style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",borderRadius:7,
                    border:`1px solid ${customMsg.channels[k]?T.green+"55":avail?T.border:T.border+"88"}`,
                    background:customMsg.channels[k]?T.greenLo:"transparent",
                    cursor:avail?"pointer":"not-allowed",fontFamily:T.font,fontSize:10,
                    color:customMsg.channels[k]?T.green:avail?T.muted:T.muted+"88",opacity:avail?1:.5}}>
                  {icon} {label} {customMsg.channels[k]?"✓":""}
                  {!avail&&<span style={{fontSize:8,color:T.muted}}>(no contact)</span>}
                </button>
              ))}
            </div>
            <div style={{marginTop:8,fontSize:9,color:T.muted}}>
              Emails: {(sendNotifModal.emails||[sendNotifModal.email]).filter(Boolean).join(", ")||"--"}
              {" - "}Mobiles: {(sendNotifModal.mobiles||[]).filter(Boolean).join(", ")||"--"}
            </div>
          </div>
          <Inp label="Subject" value={customMsg.subject} onChange={v=>setCustomMsg(p=>({...p,subject:v}))} placeholder="Notification subject"/>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:.8,textTransform:"uppercase"}}>Message Body</div>
            <textarea value={customMsg.body} onChange={e=>setCustomMsg(p=>({...p,body:e.target.value}))}
              placeholder={`Dear ${sendNotifModal.name},\n\nYour message here...\n\nTeam AURAVEX GLOBAL AI`}
              rows={5} style={{background:T.faint,border:`1px solid ${T.border}`,borderRadius:6,padding:"8px 10px",fontSize:11,color:T.text,fontFamily:T.font,outline:"none",resize:"vertical",width:"100%"}}/>
          </div>
          <AlertBox type="info">i️ Message will be sent to {sendNotifModal.name} via selected channels.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setSendNotifModal(null)}>Cancel</Btn>
            <Btn onClick={doSendCustom} disabled={!customMsg.subject||!customMsg.body}>📤 Send Notification</Btn>
          </div>
        </div>}
      </Modal>
    </div>
  );
};

/* -- SUBSCRIPTIONS ------------------------------------------------ */
export const PageSubscriptions = ({tenants,setTenants,plans,setPlans,toast,sendNotification,logAudit}) => {
  const [mgmt,setMgmt]         = useState(null);
  const [newPlan,setNewPlan]   = useState("");
  const [notify,setNotify]     = useState(true);
  const [editPlan,setEditPlan] = useState(null);   // which plan is being price-edited
  const [pf,setPf]             = useState({});      // plan edit form values
  const [delTenant,setDelTenant]=useState(null);    // confirm delete

  /* -- Change tenant plan ---------------------------------------- */
  const doChange = () => {
    const pl = plans.find(p=>p.id===newPlan);
    if(!pl) return;
    const prevPlan = plans.find(p=>p.id===mgmt.plan);
    setTenants(p=>p.map(t=>t.id===mgmt.id?{...t,plan:newPlan,maxCam:pl.cameras,maxU:pl.users,revenue:pl.monthly}:t)); apiPatch(`/api/tenants/${mgmt.id}/plan`,{plan_id:newPlan,max_cameras:pl.cameras,max_users:pl.users,revenue:pl.monthly}).catch(e=>toast&&toast("Save failed: "+e.message,"warn"));
    if(notify){
      sendNotification(mgmt.contact,
        `AURAVEX GLOBAL AI: Subscription plan changed`,
        `Dear ${mgmt.name},\n\nYour subscription plan has been changed from ${prevPlan?.name||mgmt.plan} to ${pl.name}.\n\nNew charges:\n* Monthly: ${INR(pl.monthly)}/mo\n* Yearly: ${INR(pl.yearly)}/yr\n* Cameras: ${pl.cameras} | Users: ${pl.users} | Storage: ${pl.storage}\n\nEffective immediately. Contact admin@auravex.ai for queries.\n\nTeam AURAVEX GLOBAL AI`,
        ["email","sms"]
      );
    }
    logAudit("PLAN_CHANGE", `${mgmt.name}: ${prevPlan?.name} -> ${pl.name}`, "High");
    setMgmt(null);
    toast(`${mgmt.name} moved to ${pl.name}`);
  };

  /* -- Delete tenant --------------------------------------------- */
  const doDelete = (t) => {
    setTenants(p=>p.filter(x=>x.id!==t.id));
    sendNotification(t.contact,
      `AURAVEX GLOBAL AI: Account Terminated`,
      `Dear ${t.name},\n\nYour AURAVEX GLOBAL AI account has been terminated by the administrator.\n\nAll data will be retained for 30 days before permanent deletion as per our data policy.\n\nFor queries: admin@auravex.ai`,
      ["email","sms"]
    );
    logAudit("TENANT_DELETED", t.name, "Critical");
    setDelTenant(null);
    toast(`${t.name} deleted -- notification sent`, "warn");
  };

  /* -- Edit plan pricing ----------------------------------------- */
  const openEditPlan = p => { setEditPlan(p); setPf({...p}); };
  const saveEditPlan = () => {
    const updated = {...pf, monthly:+pf.monthly, yearly:+pf.yearly, lifetime:+pf.lifetime, cameras:+pf.cameras, users:+pf.users};
    setPlans(ps=>ps.map(p=>p.id===updated.id?updated:p));
    // notify all tenants on that plan
    const affected = tenants.filter(t=>t.plan===updated.id);
    affected.forEach(t=>{
      sendNotification(t.contact,
        `AURAVEX GLOBAL AI: Subscription pricing update -- ${updated.name}`,
        `Dear ${t.name},\n\nThe pricing for your ${updated.name} plan has been updated by the administrator:\n\n* Monthly: ${INR(updated.monthly)}/mo\n* Yearly: ${INR(updated.yearly)}/yr\n* Lifetime: ${INR(updated.lifetime)}\n* Max Cameras: ${updated.cameras}\n* Max Users: ${updated.users}\n* Storage: ${updated.storage}\n* Retention: ${updated.retention}\n\nChanges effective from next billing cycle.\n\nTeam AURAVEX GLOBAL AI`,
        ["email","sms"]
      );
    });
    logAudit("PLAN_PRICING_EDITED", `${updated.name} -- ₹${updated.monthly}/mo`, "High");
    setEditPlan(null);
    toast(`${updated.name} pricing saved -- ${affected.length} tenant(s) notified`);
  };

  const fE = k => v => setPf(p=>({...p,[k]:v}));

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <PageHeader title="Subscription & Billing" sub="Admin-editable INR plans - Quota enforcement - Auto-suspend - SMS+Email notifications on changes">
        <Btn v="ghost" sm onClick={()=>{ const p=plans[0]; openEditPlan(p); }}>✏ Edit Plan Pricing</Btn>
      </PageHeader>

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        <KPI label="Total Tenants"  value={tenants.length}                                          icon="🏢" color={T.blue}/>
        <KPI label="Active"         value={tenants.filter(t=>t.status==="Active").length}           icon="✅" color={T.green}/>
        <KPI label="Platform MRR"   value={INR(tenants.filter(t=>t.status==="Active").reduce((a,t)=>{const p=plans.find(pl=>pl.id===t.plan);return a+(p?p.monthly:0);},0))} icon="💰" color={T.accent}/>
        <KPI label="Suspended"      value={tenants.filter(t=>t.status==="Suspended").length}        icon="🔒" color={T.red}/>
      </div>

      {/* Plan Cards -- each with ✏ Edit Pricing */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14}}>
        {plans.map((p,i)=>(
          <div key={p.id} style={{background:i===3?`linear-gradient(160deg,${T.purpleLo},#EDE9FD)`:T.card,border:`2px solid ${p.color}38`,borderRadius:14,padding:22,position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:p.color}}/>
            {/* Admin edit badge */}
            <div style={{position:"absolute",top:12,right:12}}>
              <button onClick={()=>openEditPlan(p)} style={{background:T.deep,border:`1px solid ${p.color}44`,borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:10,color:p.color,fontFamily:T.font,fontWeight:700}}>✏ Edit</button>
            </div>
            <div style={{fontSize:16,fontWeight:800,color:p.color,marginBottom:4}}>{p.name}</div>
            <div style={{fontSize:28,fontWeight:800,fontFamily:T.mono,color:T.text,marginBottom:4,lineHeight:1}}>{INR(p.monthly)}<span style={{fontSize:11,color:T.muted,fontWeight:400}}>/mo</span></div>
            <div style={{fontSize:10,color:T.muted,marginBottom:12}}>{INR(p.yearly)}/yr - {INR(p.lifetime)} lifetime</div>
            <div style={{fontSize:9,color:T.muted,marginBottom:10}}>
              <Badge text={`${tenants.filter(t=>t.plan===p.id).length} tenants`} color={p.color} xs/>
            </div>
            {[["Cameras",p.cameras],["Users",p.users],["Storage",p.storage],["Retention",p.retention],["SLA",p.sla],["AI Edge Server Edge",p.jetson?"✓":"✗"],["Custom Models",p.custom?"✓":"✗"],["WhatsApp",p.whatsapp?"✓":"✗"],["Support",p.support]].map(([l,v])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${T.border}`,fontSize:11}}>
                <span style={{color:T.muted}}>{l}</span>
                <span style={{fontWeight:600,color:v==="✓"?T.green:v==="✗"?T.red:T.text}}>{v}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Tenant quota table */}
      <Panel title="TENANT QUOTA & BILLING MANAGEMENT" action={<Badge text="Auto-suspend: ON" color={T.accent} sm/>}>
        <DataTable cols={[
          {k:"name",   l:"Tenant",    r:v=><span style={{fontWeight:700}}>{v}</span>},
          {k:"plan",   l:"Plan",      r:v=>{ const pl=plans.find(p=>p.id===v); return <Badge text={pl?.name||v} color={pl?.color||T.blue} sm/>; }},
          {k:"cameras",l:"Cameras",  r:(v,row)=><div style={{minWidth:100}}><div style={{display:"flex",justifyContent:"space-between",fontSize:9,marginBottom:2}}><span style={{fontFamily:T.mono}}>{v}/{row.maxCam}</span><span style={{color:v/row.maxCam>.9?T.red:T.green}}>{Math.round(v/row.maxCam*100)}%</span></div><Prog val={v} max={row.maxCam} color={T.blue} h={4}/></div>},
          {k:"storage",l:"Storage",  r:v=><div style={{minWidth:80}}><span style={{fontSize:9,fontFamily:T.mono,color:v>90?T.red:v>75?T.accent:T.green}}>{v}%</span><Prog val={v} h={4}/></div>},
          {k:"billing",l:"Billing",  r:v=><Badge text={v} xs color={T.teal}/>},
          {k:"status", l:"Status",   r:v=><Dot s={v}/>},
          {k:"revenue",l:"MRR",      r:v=><span style={{color:T.accent,fontFamily:T.mono,fontWeight:700}}>{INR(v)}</span>},
          {k:"contact",l:"Contact",  r:v=><span style={{fontSize:9,color:T.muted,fontFamily:T.mono}}>{v}</span>},
          {k:"_act",l:"Actions",r:(v,row)=><div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {(row.cameras/row.maxCam>.85||row.storage>85)&&<Btn sm v="warn" onClick={()=>{setMgmt(row);setNewPlan(row.plan);}}>^ Upgrade</Btn>}
            <Btn sm v="blue"  onClick={()=>{setMgmt(row);setNewPlan(row.plan);}}>✏ Manage</Btn>
            <Btn sm v="danger" onClick={()=>setDelTenant(row)}>🗑 Delete</Btn>
          </div>},
        ]} rows={tenants}/>
      </Panel>

      {/* -- EDIT PLAN PRICING MODAL -- */}
      <Modal open={!!editPlan} title={`✏ Edit Plan Pricing -- ${editPlan?.name}`} onClose={()=>setEditPlan(null)} wide>
        {editPlan&&(
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <AlertBox type="warn">⚠️ Changing pricing will send SMS + Email notifications to all <strong>{tenants.filter(t=>t.plan===editPlan.id).length} tenant(s)</strong> on this plan.</AlertBox>
            {/* Pricing */}
            <Panel title="PRICING (₹ INR)">
              <Grid cols={3} gap={12}>
                <Inp label="Monthly Price (₹)"  value={String(pf.monthly||"")}  onChange={fE("monthly")}  placeholder="4999"/>
                <Inp label="Yearly Price (₹)"   value={String(pf.yearly||"")}   onChange={fE("yearly")}   placeholder="49999"/>
                <Inp label="Lifetime Price (₹)" value={String(pf.lifetime||"")} onChange={fE("lifetime")} placeholder="149999"/>
              </Grid>
            </Panel>
            {/* Limits */}
            <Panel title="PLAN LIMITS">
              <Grid cols={3} gap={12}>
                <Inp label="Max Cameras"     value={String(pf.cameras||"")} onChange={fE("cameras")}   placeholder="8"/>
                <Inp label="Max Users"       value={String(pf.users||"")}   onChange={fE("users")}     placeholder="5"/>
                <Inp label="Storage"         value={String(pf.storage||"")} onChange={fE("storage")}   placeholder="200 GB"/>
                <Inp label="Retention"       value={String(pf.retention||"")} onChange={fE("retention")} placeholder="30 days"/>
                <Inp label="SLA"             value={String(pf.sla||"")}     onChange={fE("sla")}       placeholder="99.0%"/>
                <Inp label="API Quota"       value={String(pf.api||"")}     onChange={fE("api")}       placeholder="1 Lakh/mo"/>
              </Grid>
            </Panel>
            {/* Features */}
            <Panel title="FEATURES">
              <Grid cols={2} gap={12}>
                <Inp label="Support Tier"     value={String(pf.support||"")} onChange={fE("support")} placeholder="Email Only"/>
                <Sel label="AI Edge Server Edge"      value={pf.jetson?"yes":"no"}  onChange={v=>setPf(p=>({...p,jetson:v==="yes"}))} options={["yes","no"]}/>
                <Sel label="Custom Models"    value={pf.custom?"yes":"no"}  onChange={v=>setPf(p=>({...p,custom:v==="yes"}))} options={["yes","no"]}/>
                <Sel label="WhatsApp Alerts"  value={pf.whatsapp?"yes":"no"} onChange={v=>setPf(p=>({...p,whatsapp:v==="yes"}))} options={["yes","no"]}/>
              </Grid>
            </Panel>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <Btn v="ghost" onClick={()=>setEditPlan(null)}>Cancel</Btn>
              <Btn v="success" onClick={saveEditPlan}>💾 Save & Notify Tenants</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* -- MANAGE TENANT PLAN MODAL -- */}
      <Modal open={!!mgmt} title={`Manage Subscription -- ${mgmt?.name}`} onClose={()=>setMgmt(null)}>
        {mgmt&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{background:T.deep,borderRadius:8,padding:12,fontSize:11,display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {[["Current Plan",plans.find(p=>p.id===mgmt.plan)?.name||mgmt.plan],["Contact",mgmt.contact],["Cameras",`${mgmt.cameras}/${mgmt.maxCam}`],["Storage",`${mgmt.storage}%`],["Billing",mgmt.billing],["Status",mgmt.status]].map(([l,v])=>(
              <div key={l}><span style={{color:T.muted}}>{l}: </span><span style={{fontWeight:700}}>{v}</span></div>
            ))}
          </div>
          <Sel label="Change Plan To" value={newPlan} onChange={setNewPlan} options={plans.map(p=>({v:p.id,l:`${p.name} -- ${INR(p.monthly)}/mo - ${p.cameras} cams`}))}/>
          {newPlan&&newPlan!==mgmt.plan&&(()=>{
            const np=plans.find(p=>p.id===newPlan);
            return np&&<AlertBox type={np.monthly>=(plans.find(p=>p.id===mgmt.plan)?.monthly||0)?"info":"warn"}>
              {np.monthly>=(plans.find(p=>p.id===mgmt.plan)?.monthly||0)?"^ Upgrade":"v Downgrade"}: {plans.find(p=>p.id===mgmt.plan)?.name} -> {np.name} - New MRR: {INR(np.monthly)}/mo
            </AlertBox>;
          })()}
          <div style={{display:"flex",alignItems:"center",gap:8,background:T.deep,borderRadius:8,padding:"10px 12px"}}>
            <input type="checkbox" checked={notify} onChange={e=>setNotify(e.target.checked)} id="notifChk" style={{accentColor:T.accent}}/>
            <label htmlFor="notifChk" style={{fontSize:11,color:T.text,cursor:"pointer"}}>📧 Send SMS + Email notification to <strong>{mgmt.contact}</strong></label>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setMgmt(null)}>Cancel</Btn>
            <Btn onClick={doChange} disabled={!newPlan}>Apply Plan Change</Btn>
          </div>
        </div>}
      </Modal>

      {/* -- DELETE TENANT CONFIRM -- */}
      <Modal open={!!delTenant} title="⚠️ Confirm Delete Tenant" onClose={()=>setDelTenant(null)}>
        {delTenant&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
          <AlertBox type="error">🗑 You are about to permanently delete <strong>{delTenant.name}</strong>. This will remove all their cameras, data, and access. This action CANNOT be undone.</AlertBox>
          <div style={{background:T.deep,borderRadius:8,padding:12,fontSize:11,display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {[["Tenant",delTenant.name],["Plan",plans.find(p=>p.id===delTenant.plan)?.name||""],["Cameras",delTenant.cameras],["MRR",INR(delTenant.revenue)],["Contact",delTenant.contact],["Status",delTenant.status]].map(([l,v])=>(
              <div key={l}><span style={{color:T.muted}}>{l}: </span><span style={{fontWeight:700,color:T.text}}>{v}</span></div>
            ))}
          </div>
          <AlertBox type="info">i️ A termination SMS + Email notification will be sent to {delTenant.contact} automatically.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setDelTenant(null)}>Cancel</Btn>
            <Btn v="danger" onClick={()=>doDelete(delTenant)}>🗑 Confirm Delete & Notify</Btn>
          </div>
        </div>}
      </Modal>
    </div>
  );
};

/* -- CO2 & SUSTAINABILITY ----------------------------------------- */
export const PageCO2 = ({toast=()=>{}}) => (
  <div style={{display:"flex",flexDirection:"column",gap:14}}>
    <PageHeader title="CO2 & Fuel Saving Analytics" sub="Idle vehicle detection - Fuel estimation - Carbon tracking - ISO 14001 - ESG scoring - Government audit-ready">
      <Badge text="ESG MODULE" color={T.green} sm/>
      <Btn v="ghost" sm onClick={()=>toast("📄 ISO 14001 report generating...")}>ISO 14001 Report</Btn>
      <Btn v="success" sm onClick={()=>toast("✅ ESG Certificate download started")}>ESG Certificate</Btn>
    </PageHeader>
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
      <KPI label="CO2 Saved MTD"    value="8.6 t"   sub="v 22% vs last month" icon="🌿" color={T.green}  trend={22}/>
      <KPI label="Fuel Saved MTD"   value="358 L"   sub="Fleet idle reduction" icon="⛽" color={T.accent}/>
      <KPI label="Idle Time Reduced" value="190 hrs" sub="All monitored zones"  icon="🚛" color={T.blue}/>
      <KPI label="ESG Score"         value="A+"      sub="Top 4% of industry"   icon="🏆" color={T.purple}/>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"3fr 1fr",gap:14}}>
      <Panel title="CO2 REDUCTION -- 6 MONTH TREND">
        <ResponsiveContainer width="100%" height={190}>
          <ComposedChart data={co2Data}>
            <defs><linearGradient id="cog" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={T.green} stopOpacity={.35}/><stop offset="95%" stopColor={T.green} stopOpacity={0}/></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
            <XAxis dataKey="m" tick={{fill:T.muted,fontSize:9}} tickLine={false}/>
            <YAxis yAxisId="l" tick={{fill:T.muted,fontSize:9}} tickLine={false}/>
            <YAxis yAxisId="r" orientation="right" tick={{fill:T.muted,fontSize:9}} tickLine={false}/>
            <Tooltip contentStyle={{background:T.panel,border:`1px solid ${T.border}`,fontSize:10}}/>
            <Legend wrapperStyle={{fontSize:10,color:T.muted}}/>
            <Area yAxisId="l" type="monotone" dataKey="co2"  stroke={T.green}  fill="url(#cog)" strokeWidth={2} name="CO2 (t)"/>
            <Bar  yAxisId="r"                 dataKey="fuel"  fill={T.accent}   fillOpacity={.6} radius={[2,2,0,0]} name="Fuel Saved (L)"/>
          </ComposedChart>
        </ResponsiveContainer>
      </Panel>
      <Panel title="SAVINGS BREAKDOWN">
        <div style={{display:"flex",flexDirection:"column",gap:10,paddingTop:4}}>
          {[["Idle Detection",38,T.accent],["Route Optimisation",29,T.blue],["Traffic Signal AI",22,T.teal],["Fleet Management",11,T.purple]].map(([l,v,c])=>(
            <div key={l}><div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}><span style={{fontSize:11}}>{l}</span><span style={{fontSize:11,color:c,fontWeight:700,fontFamily:T.mono}}>{v}%</span></div><Prog val={v} max={50} color={c} h={5}/></div>
          ))}
        </div>
      </Panel>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
      <Panel title="MONTHLY COST SAVINGS (₹)">
        <ResponsiveContainer width="100%" height={155}>
          <AreaChart data={co2Data}>
            <defs><linearGradient id="sg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={T.accent} stopOpacity={.35}/><stop offset="95%" stopColor={T.accent} stopOpacity={0}/></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/><XAxis dataKey="m" tick={{fill:T.muted,fontSize:9}}/><YAxis tick={{fill:T.muted,fontSize:9}}/>
            <Tooltip contentStyle={{background:T.panel,border:`1px solid ${T.border}`,fontSize:10}} formatter={v=>[`₹${Number(v).toLocaleString("en-IN")}`]}/>
            <Area type="monotone" dataKey="saved" stroke={T.accent} fill="url(#sg)" strokeWidth={2} name="₹ Savings"/>
          </AreaChart>
        </ResponsiveContainer>
      </Panel>
      <Panel title="IDLE TIME REDUCTION (HRS)">
        <ResponsiveContainer width="100%" height={155}>
          <BarChart data={co2Data}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/><XAxis dataKey="m" tick={{fill:T.muted,fontSize:9}}/><YAxis tick={{fill:T.muted,fontSize:9}}/>
            <Tooltip contentStyle={{background:T.panel,border:`1px solid ${T.border}`,fontSize:10}}/>
            <Bar dataKey="idle" fill={T.blue} radius={[3,3,0,0]} name="Idle Hrs Reduced"/>
          </BarChart>
        </ResponsiveContainer>
      </Panel>
    </div>
    <div style={{background:"linear-gradient(135deg,#EDFDF5,#D4F5E5)",border:`1px solid ${T.green}44`,borderRadius:10,padding:20,display:"flex",alignItems:"center",gap:20}}>
      <span style={{fontSize:50}}>🌍</span>
      <div style={{flex:1}}>
        <div style={{fontSize:15,fontWeight:800,color:T.green}}>Carbon Neutral Commitment -- On Track</div>
        <div style={{fontSize:11,color:T.muted,marginTop:5,lineHeight:1.8}}>Tracking <strong style={{color:T.green}}>28 active ESG KPIs</strong> across 6 tenants. Projection: <strong style={{color:T.green}}>42% CO2 reduction</strong> by year-end. Cumulative fuel savings: <strong style={{color:T.accent}}>₹2.84 Lakh YTD</strong>. ISO 14001 reports auto-generated monthly, government-audit ready.</div>
      </div>
      <div style={{textAlign:"center",minWidth:80}}>
        <div style={{fontSize:40,fontWeight:800,color:T.green,fontFamily:T.mono}}>A+</div>
        <div style={{fontSize:9,color:T.muted,letterSpacing:1}}>ESG RATING</div>
      </div>
    </div>
  </div>
);
/* ================================================================
   PAGES -- Part A  (Reports, Infrastructure, Audit, AI Copilot)
================================================================ */

/* -- REPORTS ------------------------------------------------------ */
export const PageReports = ({reports,setReports,tenants,toast,logAudit}) => {
  const [showCreate,setShowCreate] = useState(false);
  const [editR,setEditR]           = useState(null);
  const [delR,setDelR]             = useState(null);
  const [form,setForm] = useState({name:"",type:"Safety",tenant:"All",format:"PDF",scheduled:false,recipients:""});
  const ff = k => v => setForm(p=>({...p,[k]:v}));
  const ef = k => v => setEditR(p=>({...p,[k]:v}));
  const create = () => {
    setReports(p=>[...p,{id:`RPT-${String(p.length+1).padStart(3,"0")}`,...form,status:"Processing",period:"Jul 2025",generated:""}]);
    logAudit&&logAudit("REPORT_CREATED",form.name,"Info");
    setShowCreate(false);
    setForm({name:"",type:"Safety",tenant:"All",format:"PDF",scheduled:false,recipients:""});
    toast("Report queued for generation");
  };
  const saveEdit=()=>{ setReports(p=>p.map(r=>r.id===editR.id?editR:r)); logAudit&&logAudit("REPORT_EDITED",editR.name,"Info"); setEditR(null); toast(`${editR.name} updated`); };
  const delReport=()=>{ setReports(p=>p.filter(r=>r.id!==delR.id)); logAudit&&logAudit("REPORT_DELETED",delR.name,"Info"); setDelR(null); toast(`${delR.name} deleted`,"warn"); };
  const reportTypes=[{t:"Traffic",c:T.orange},{t:"Safety",c:T.accent},{t:"Incident",c:T.red},{t:"Analytics",c:T.blue},{t:"ESG",c:T.green},{t:"Technical",c:T.purple}];
  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <PageHeader title="Reporting Engine" sub="Standard reports - Custom templates - Scheduled delivery - PDF / Excel / CSV - Email + WhatsApp distribution">
        <Btn v="ghost" sm onClick={()=>setShowCreate(true)}>+ Custom Report</Btn>
        <Btn sm onClick={()=>toast("⏰ Report scheduling -- coming soon")}>⏰ Schedule Reports</Btn>
      </PageHeader>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        <KPI label="Total Reports"   value={reports.length}                                  icon="📊" color={T.blue}/>
        <KPI label="Ready to Download" value={reports.filter(r=>r.status==="Ready").length}  icon="✅" color={T.green}/>
        <KPI label="Scheduled"       value={reports.filter(r=>r.scheduled).length}           icon="⏰" color={T.accent}/>
        <KPI label="Processing"      value={reports.filter(r=>r.status==="Processing").length} icon="⏳" color={T.orange}/>
      </div>
      {/* accuracy-trend / radar charts removed 2026-11-04: they were static sample data */}
      {/* Report type cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:10}}>
        {reportTypes.map(({t,c})=>(
          <div key={t} style={{background:T.card,border:`1px solid ${c}30`,borderRadius:9,padding:"12px 10px",textAlign:"center",cursor:"pointer"}}
            onMouseEnter={e=>e.currentTarget.style.background=T.hi}
            onMouseLeave={e=>e.currentTarget.style.background=T.card}>
            <div style={{fontSize:22,marginBottom:6}}>{{Traffic:"🚦",Safety:"⛑️",Incident:"🚨",Analytics:"📈",ESG:"🌿",Technical:"🔧"}[t]}</div>
            <div style={{fontSize:11,fontWeight:700,color:c}}>{t}</div>
            <div style={{fontSize:9,color:T.muted,marginTop:2}}>{reports.filter(r=>r.type===t).length} reports</div>
          </div>
        ))}
      </div>
      {/* Report list */}
      <Panel title="REPORT LIBRARY">
        <DataTable cols={[
          {k:"id",         l:"Report ID",   r:v=><span style={{fontFamily:T.mono,color:T.blue,fontSize:10}}>{v}</span>},
          {k:"name",       l:"Report Name", r:v=><span style={{fontWeight:700}}>{v}</span>},
          {k:"type",       l:"Type",        r:v=><Badge text={v} sm color={{Traffic:T.orange,Safety:T.accent,Incident:T.red,Analytics:T.blue,ESG:T.green,Technical:T.purple}[v]||T.blue}/>},
          {k:"tenant",     l:"Tenant",      r:v=><span style={{fontSize:10,color:T.muted}}>{v==="All"?"All Tenants":(tenants||[]).find(t=>t.id===v)?.name||v}</span>},
          {k:"period",     l:"Period",      r:v=><span style={{fontSize:10,fontFamily:T.mono}}>{v}</span>},
          {k:"format",     l:"Format",      r:v=><Badge text={v} xs color={{PDF:T.red,XLSX:T.green,CSV:T.blue}[v]||T.muted}/>},
          {k:"scheduled",  l:"Scheduled",   r:v=>v?<Badge text="AUTO" color={T.green} xs/>:<span style={{color:T.muted,fontSize:10}}>Manual</span>},
          {k:"status",     l:"Status",      r:v=><Dot s={v}/>},
          {k:"recipients", l:"Recipients",  r:v=><span style={{fontSize:9,color:T.muted,maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",display:"block"}}>{v||"--"}</span>},
          {k:"_act",l:"Actions",r:(v,row)=><div style={{display:"flex",gap:4}}>
            {row.status==="Ready"&&<Btn sm v="success" onClick={()=>toast("📥 Report "+row.id+" downloaded","success")}>v Download</Btn>}
            <Btn sm v="ghost" onClick={()=>setEditR({...row})}>✏ Edit</Btn>
            <Btn sm v="ghost" onClick={()=>toast("📧 Report emailed to admin")}>📧 Email</Btn>
            <Btn sm v="teal"  onClick={()=>toast("📱 Report shared via WhatsApp")}>📱 WhatsApp</Btn>
            <Btn sm v="danger" onClick={()=>setDelR(row)}>🗑</Btn>
          </div>},
        ]} rows={reports}/>
      </Panel>

      {/* Edit Report Modal */}
      <Modal open={!!editR} title={`✏ Edit Report -- ${editR?.name}`} onClose={()=>setEditR(null)}>
        {editR&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Inp label="Report Name" value={editR.name||""} onChange={ef("name")} placeholder="Report name"/>
          <Grid cols={2} gap={12}>
            <Sel label="Type"    value={editR.type||"Safety"} onChange={ef("type")}   options={["Safety","Traffic","Incident","Analytics","ESG","Technical","Custom"]}/>
            <Sel label="Format"  value={editR.format||"PDF"}  onChange={ef("format")} options={["PDF","XLSX","CSV","JSON"]}/>
            <Sel label="Status"  value={editR.status||"Ready"} onChange={ef("status")} options={["Ready","Processing","Scheduled","Error"]}/>
            <Inp label="Recipients" value={editR.recipients||""} onChange={ef("recipients")} placeholder="email@org.in"/>
          </Grid>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setEditR(null)}>Cancel</Btn>
            <Btn onClick={saveEdit}>💾 Save Changes</Btn>
          </div>
        </div>}
      </Modal>

      {/* Delete Report Confirm */}
      <Modal open={!!delR} title="⚠️ Delete Report" onClose={()=>setDelR(null)}>
        {delR&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
          <AlertBox type="error">🗑 Delete report <strong>{delR.name}</strong>? The generated file will be permanently removed.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setDelR(null)}>Cancel</Btn>
            <Btn v="danger" onClick={delReport}>🗑 Confirm Delete</Btn>
          </div>
        </div>}
      </Modal>

      {/* Custom report modal */}
      <Modal open={showCreate} title="Create Custom Report" onClose={()=>setShowCreate(false)}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Inp label="Report Name" value={form.name} onChange={ff("name")} placeholder="Monthly PPE Compliance -- Tata Motors"/>
          <Grid cols={2} gap={12}>
            <Sel label="Report Type"     value={form.type}   onChange={ff("type")}   options={["Safety","Traffic","Incident","Analytics","ESG","Technical","Custom"]}/>
            <Sel label="Tenant Scope"    value={form.tenant} onChange={ff("tenant")} options={[{v:"All",l:"All Tenants"},...(tenants||[]).map(t=>({v:t.id,l:t.name}))]}/>
            <Sel label="Output Format"   value={form.format} onChange={ff("format")} options={["PDF","XLSX","CSV","JSON"]}/>
            <Sel label="Date Range"      value="Last Month"  onChange={()=>{}}       options={["Last 7 Days","Last Month","Last Quarter","Last Year","Custom Range"]}/>
            <Sel label="Include Charts"  value="Yes"         onChange={()=>{}}       options={["Yes","No"]}/>
            <Sel label="Schedule"        value="Once"        onChange={()=>{}}       options={["Once","Daily","Weekly","Monthly","Quarterly"]}/>
          </Grid>
          <Inp label="Email Recipients (comma separated)" value={form.recipients} onChange={ff("recipients")} placeholder="admin@org.in, supervisor@org.in"/>
          <AlertBox type="info">i️ Custom reports are generated using your platform data. PDF reports include charts, tables and executive summary. XLSX exports include raw data with pivot-ready formatting.</AlertBox>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="ghost" onClick={()=>setShowCreate(false)}>Cancel</Btn>
            <Btn onClick={create} disabled={!form.name}>Generate Report</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
};
